import { jsPDF } from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import {
  RevisionStatusEnum,
  VehicleRevisionDetail,
  VehicleRevisionHistoryEntry,
  VehicleRevisionListItem,
} from '@/app/modules/vehicle-revision/core/type';

export interface RevisionPdfLogo {
  base64: string | null;
  width: number;
  height: number;
}

export interface RevisionListPdfOptions {
  items: VehicleRevisionListItem[];
  generatedBy: string;
  filters: string[];
  logo: RevisionPdfLogo;
  getStatusLabel: (status: RevisionStatusEnum) => string;
  getKmRemainingLabel: (item: VehicleRevisionListItem) => string;
}

export interface RevisionDetailPdfOptions {
  detail: VehicleRevisionDetail;
  generatedBy: string;
  logo: RevisionPdfLogo;
  getStatusLabel: (status: RevisionStatusEnum) => string;
  getKmHighlightLabel: () => string;
  getKmHighlightValue: () => string;
}

const BRAND_RED: [number, number, number] = [192, 20, 44];

export function loadRevisionPdfLogo(url: string): Promise<RevisionPdfLogo> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = url;
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve({ base64: null, width: 0, height: 0 });
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve({
        base64: canvas.toDataURL('image/png'),
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = () => resolve({ base64: null, width: 0, height: 0 });
  });
}

function formatKm(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value.toLocaleString('pt-BR')} km`;
}

function addPdfHeader(
  doc: jsPDF,
  title: string,
  generatedBy: string,
  filters: string[],
  logo: RevisionPdfLogo,
): number {
  const pageWidth = doc.internal.pageSize.width;
  const marginX = 14;
  let currentY = 10;
  let textY = currentY + 5;

  const maxLogoWidth = 25;
  const maxLogoHeight = 15;

  if (logo.base64) {
    let finalLogoWidth = maxLogoWidth;
    let finalLogoHeight = maxLogoHeight;

    if (logo.width > 0 && logo.height > 0) {
      const ratio = Math.min(
        maxLogoWidth / logo.width,
        maxLogoHeight / logo.height,
      );
      finalLogoWidth = logo.width * ratio;
      finalLogoHeight = logo.height * ratio;
    }

    doc.addImage(
      logo.base64,
      'PNG',
      pageWidth - marginX - finalLogoWidth,
      currentY,
      finalLogoWidth,
      finalLogoHeight,
    );
  }

  doc.setTextColor(0);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, marginX, textY);
  textY += 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, marginX, textY);
  textY += 5;
  doc.text(`Gerado por: ${generatedBy}`, marginX, textY);
  textY += 5;

  const filterText =
    filters.length > 0
      ? `Filtros: ${filters.join(', ')}`
      : 'Filtros: Nenhum';
  const maxTextWidth = pageWidth - marginX * 2 - 35;
  const splitFilters = doc.splitTextToSize(filterText, maxTextWidth);
  doc.text(splitFilters, marginX, textY);
  textY += splitFilters.length * 5;

  return Math.max(currentY + 20, textY) + 5;
}

function statusTextColor(status: RevisionStatusEnum): [number, number, number] {
  switch (status) {
    case RevisionStatusEnum.OVERDUE:
      return [207, 19, 34];
    case RevisionStatusEnum.NOT_CONFIGURED:
      return [212, 136, 6];
    default:
      return [56, 158, 13];
  }
}

export function exportRevisionListPdf(options: RevisionListPdfOptions): void {
  const doc = new jsPDF('l', 'mm', 'a4');
  const tableStartY = addPdfHeader(
    doc,
    'Revisões Preventivas — Veículos',
    options.generatedBy,
    options.filters,
    options.logo,
  );

  const head = [
    ['Placa', 'Modelo', 'KM atual', 'Próxima revisão', 'Faltam / Atraso', 'Status'],
  ];

  const body: RowInput[] = options.items.map((item) => [
    item.chassis.toUpperCase(),
    item.product_name || '-',
    formatKm(item.current_km),
    formatKm(item.next_revision_km),
    options.getKmRemainingLabel(item),
    options.getStatusLabel(item.revision_status),
  ]);

  autoTable(doc, {
    startY: tableStartY,
    head,
    body,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 4,
      valign: 'middle',
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: BRAND_RED,
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 28, halign: 'center' },
      1: { cellWidth: 40 },
      2: { cellWidth: 28, halign: 'center' },
      3: { cellWidth: 32, halign: 'center' },
      4: { cellWidth: 35, halign: 'center' },
      5: { cellWidth: 30, halign: 'center' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 5) {
        const item = options.items[data.row.index];
        if (item) {
          const color = statusTextColor(item.revision_status);
          data.cell.styles.textColor = color;
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
  });

  doc.save(`revisoes-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function exportRevisionDetailPdf(options: RevisionDetailPdfOptions): void {
  const { detail } = options;
  const doc = new jsPDF('p', 'mm', 'a4');
  const title = `Revisão — ${detail.chassis.toUpperCase()}`;
  const tableStartY = addPdfHeader(
    doc,
    title,
    options.generatedBy,
    [],
    options.logo,
  );

  const summaryBody: RowInput[] = [
    ['Modelo', detail.product_name || '-'],
    ['Placa', detail.chassis.toUpperCase()],
    ['KM atual (checklist)', formatKm(detail.current_km)],
    [
      'Último checklist',
      detail.last_checklist_at
        ? new Date(detail.last_checklist_at).toLocaleString('pt-BR')
        : '—',
    ],
    ['Status', options.getStatusLabel(detail.revision_status)],
    [options.getKmHighlightLabel(), options.getKmHighlightValue()],
    ['KM última revisão', formatKm(detail.last_revision_km)],
    ['Intervalo', formatKm(detail.revision_interval_km)],
    ['Próxima revisão', formatKm(detail.next_revision_km)],
  ];

  autoTable(doc, {
    startY: tableStartY,
    body: summaryBody,
    theme: 'striped',
    styles: { fontSize: 10, cellPadding: 4 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 55 },
      1: { cellWidth: 'auto' },
    },
    headStyles: {
      fillColor: BRAND_RED,
      textColor: 255,
    },
  });

  const historyStartY =
    (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable
      ?.finalY ?? tableStartY;
  const historyY = historyStartY + 10;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Histórico de revisões', 14, historyY);

  const history = detail.history ?? [];
  const historyHead = [['Data', 'KM revisão', 'Intervalo vigente', 'Registrado por']];
  const historyBody: RowInput[] = history.map(
    (entry: VehicleRevisionHistoryEntry) => [
      new Date(entry.createdAt).toLocaleString('pt-BR'),
      formatKm(entry.revision_km),
      formatKm(entry.interval_km),
      entry.created_by_user_name || '-',
    ],
  );

  autoTable(doc, {
    startY: historyY + 4,
    head: historyHead,
    body: historyBody.length ? historyBody : [['—', '—', '—', 'Nenhum registro']],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: {
      fillColor: BRAND_RED,
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
    },
    columnStyles: {
      0: { cellWidth: 45 },
      1: { cellWidth: 35, halign: 'center' },
      2: { cellWidth: 40, halign: 'center' },
      3: { cellWidth: 'auto' },
    },
  });

  doc.save(`revisao-${detail.chassis.toUpperCase()}.pdf`);
}
